/*
==================================================
CART MOBILE FOOTER

Функция:
общий нижний блок навигации для всех шагов mobile cart modal

Родитель:
cart-mobile-modal/index.tsx

Дочерние элементы:
кнопки Checkout / Back / Next / Place order

Использует:
текущий step
внешние обработчики навигации

Примечания:
footer централизует нижние кнопки и убирает их из step-блоков
==================================================
*/

"use client";

import { triggerHaptic } from "../../../../lib/custom-ui/haptics/haptics";

type MobileModalStep =
  | "cart"
  | "address"
  | "shipping"
  | "payment"
  | "review";

type CartMobileFooterProps = {
  step: MobileModalStep;
  hasCartItems: boolean;
  isSubmittingAddress?: boolean;
  onCartCheckout: () => void;
  onAddressBack: () => void;
  onAddressNext: () => void;
  onShippingBack: () => void;
  onShippingNext: () => void;
  onPaymentBack: () => void;
  onPaymentNext: () => void;
  onReviewBack: () => void;
  onReviewContinue: () => void;
};

function PrimaryButton({
  children,
  onClick,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        triggerHaptic(10);
        onClick();
      }}
      disabled={disabled}
      className="flex h-11 flex-1 items-center justify-center rounded-xl border border-white/25 text-sm font-bold text-white shadow-lg active:scale-95 disabled:opacity-70"
      style={{
        background:
          "linear-gradient(90deg,#17365b 0%,#245b93 48%,#3794e6 100%)",
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        triggerHaptic(10);
        onClick();
      }}
      className="flex h-11 flex-1 items-center justify-center rounded-xl border border-slate-300 bg-white text-sm font-bold text-slate-700 shadow-sm active:scale-95"
    >
      {children}
    </button>
  );
}

export default function CartMobileFooter({
  step,
  hasCartItems,
  isSubmittingAddress = false,
  onCartCheckout,
  onAddressBack,
  onAddressNext,
  onShippingBack,
  onShippingNext,
  onPaymentBack,
  onPaymentNext,
  onReviewBack,
  onReviewContinue,
}: CartMobileFooterProps) {
  if (!hasCartItems) return null;

  if (step === "cart") {
    return (
      <div className="px-3 pb-4 pt-4">
        <button
          type="button"
          onClick={() => {
            triggerHaptic(10);
            onCartCheckout();
          }}
          className="relative mx-auto flex h-11 w-[250px] items-center justify-center rounded-full border border-white/30 bg-gradient-to-b from-green-500 to-green-700 text-[0.95rem] font-bold uppercase tracking-[.05em] text-white shadow-[0_6px_14px_rgba(0,0,0,0.5)] transition hover:brightness-110 active:scale-[0.97]"
        >
          Checkout
        </button>
      </div>
    );
  }

  if (step === "address") {
    return (
      <div className="flex gap-3 px-3 pb-4 pt-4">
        <SecondaryButton onClick={onAddressBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onAddressNext} disabled={isSubmittingAddress}>
          {isSubmittingAddress ? "Saving..." : "Next"}
        </PrimaryButton>
      </div>
    );
  }

  if (step === "shipping") {
    return (
      <div className="flex gap-3 px-3 pb-4 pt-4">
        <SecondaryButton onClick={onShippingBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onShippingNext}>Next</PrimaryButton>
      </div>
    );
  }

  if (step === "payment") {
    return (
      <div className="flex gap-3 px-3 pb-4 pt-4">
        <SecondaryButton onClick={onPaymentBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onPaymentNext}>Next</PrimaryButton>
      </div>
    );
  }

  if (step === "review") {
    return (
      <div className="flex gap-3 px-3 pb-4 pt-4">
        <SecondaryButton onClick={onReviewBack}>Back</SecondaryButton>
        <PrimaryButton onClick={onReviewContinue}>Place order</PrimaryButton>
      </div>
    );
  }

  return null;
}
